import React, { useState } from 'react';
import { apiLogin, createAdmin, getAdmins, removeAdmin, requestEmailOTP, requestOTP, resetEmailWithPassword, updateAdmin, verifyCurrentEmailOTP, verifyEmailOTP, verifyOTPChangePassword } from '../storage';
import { exportJSON, importJSON } from '../db';
import { Avatar, Badge, Btn, Card, CardHead, Field, Grid, Input, Modal, ModalFooter, PhotoZone, Select, Span, Tabs, toast } from '../ui.jsx';

function Settings({ db, save, user, setUser }) {
  const sanitizeSettings = (s) => ({ ...s, addr: (s.addr && s.addr.trim() !== 'admin') ? s.addr : '' });
  const [form, setForm] = useState(() => sanitizeSettings({ ...db.settings }));
  const [auOpen, setAuOpen] = useState(false);
  const [auForm, setAuForm] = useState({});
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  // emailOtpStep: false | 'verify-current' | 'enter-new' | 'verify-new' | 'recover'
  const [emailOtpStep, setEmailOtpStep] = useState(false);
  const [emailOtpVal, setEmailOtpVal] = useState('');
  const [emailOtpSending, setEmailOtpSending] = useState(false);
  const [emailNew, setEmailNew] = useState('');
  const [emailRecoveryPw, setEmailRecoveryPw] = useState('');
  const [emailPrev, setEmailPrev] = useState(''); // store previous email for revert
  const [cpOld, setCpOld] = useState(''); const [cpNew, setCpNew] = useState(''); const [cpConf, setCpConf] = useState('');
  const [cpOtpStep, setCpOtpStep] = useState(false); // false = form, true = otp entry
  const [cpOtp, setCpOtp] = useState('');
  const [cpOtpSending, setCpOtpSending] = useState(false);
  const [cpOtpEmail, setCpOtpEmail] = useState(''); const isMainAdmin = user.username === 'admin';

  const sf = k => v => { const nf = sanitizeSettings({ ...form, [k]: v }); setForm(nf); save({ ...db, settings: nf }); };

  const addUser = async () => {
    if (!auForm.u?.trim() || !auForm.p) { toast('Username and password required', 'err'); return; }
    if (auForm.p.length < 6) { toast('Min 6 chars', 'err'); return; }
    try {
      await createAdmin({ username: auForm.u, password: auForm.p, name: auForm.n || auForm.u, role: auForm.r || 'Admin' });
      const admins = await getAdmins();
      save({ ...db, admins });
      setAuOpen(false); setAuForm({}); toast('User added');
    } catch (err) { toast(err.message, 'err'); }
  };

  const delUser = async (a) => {
    if (!window.confirm('Remove?')) return;
    try {
      await removeAdmin(a._id || a.id);
      const admins = await getAdmins();
      save({ ...db, admins });
      toast('User removed');
    } catch (err) { toast(err.message, 'err'); }
  };

  const openEdit = a => { setEditForm({ ...a }); setEditOpen(true); setEmailOtpStep(false); setEmailOtpVal(''); setEmailNew(''); };
  const saveEdit = async () => {
    if (!editForm.name?.trim()) { toast('Name required', 'err'); return; }
    try {
      await updateAdmin(editForm._id || editForm.id, {
        name: editForm.name,
        role: editForm.role,
        photo: editForm.pic || editForm.photo || '',
      });
      const admins = await getAdmins();
      save({ ...db, admins });
      if (editForm.username === user.username) {
        const updatedUser = { ...user, name: editForm.name, role: editForm.role, pic: editForm.pic || editForm.photo || user.pic || '' };
        setUser(updatedUser);
      }
      setEditOpen(false); setEmailOtpStep(false); toast('Profile updated');
    } catch (err) { toast(err.message, 'err'); }
  };

  const resetEmailFlow = () => { setEmailOtpStep(false); setEmailOtpVal(''); setEmailNew(''); setEmailRecoveryPw(''); };

  const sendEmailChangeOtp = async () => {
    if (!editForm.email) { setEmailOtpStep('enter-new'); return; }
    try {
      setEmailOtpSending(true);
      setEmailPrev(editForm.email);
      const res = await requestEmailOTP();
      setEmailOtpStep('verify-current');
      toast(`OTP sent to ${res.email}`);
    } catch (err) { toast(err.message || 'Failed to send OTP', 'err'); }
    finally { setEmailOtpSending(false); }
  };

  const verifyCurrentAndSendNew = async () => {
    if (!emailOtpVal) { toast('Enter OTP', 'err'); return; }
    if (!emailNew) { toast('Enter new email', 'err'); return; }
    try {
      await verifyCurrentEmailOTP(emailOtpVal, emailNew);
      setEmailOtpVal('');
      setEmailOtpStep('verify-new');
      toast(`OTP sent to ${emailNew}`);
    } catch (err) { toast(err.message, 'err'); }
  };

  const verifyEmailAndSave = async () => {
    if (!emailOtpVal) { toast('Enter OTP', 'err'); return; }
    try {
      await verifyEmailOTP(emailOtpVal, emailNew);
      const admins = await getAdmins();
      save({ ...db, admins });
      setEditForm(f => ({ ...f, email: emailNew }));
      resetEmailFlow();
      toast('Email updated successfully');
    } catch (err) { toast(err.message, 'err'); }
  };

  const recoverEmail = async () => {
    if (!emailRecoveryPw || !emailNew) { toast('Enter password and new email', 'err'); return; }
    try {
      await resetEmailWithPassword(emailRecoveryPw, emailNew);
      const admins = await getAdmins();
      save({ ...db, admins });
      setEditForm(f => ({ ...f, email: emailNew }));
      resetEmailFlow();
      toast('Email updated');
    } catch (err) { toast(err.message, 'err'); }
  };

  const saveEmailDirect = async () => {
    if (!emailNew) { toast('Enter email', 'err'); return; }
    try {
      await updateAdmin(editForm._id || editForm.id, { email: emailNew });
      const admins = await getAdmins();
      save({ ...db, admins });
      setEditForm(f => ({ ...f, email: emailNew }));
      resetEmailFlow();
      toast('Email saved');
    } catch (err) { toast(err.message, 'err'); }
  };

  const handlePicUpload = e => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > 500 * 1024) { toast('Image too large (max 500KB)', 'err'); return; }
    const reader = new FileReader();
    reader.onload = ev => setEditForm(f => ({ ...f, pic: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const chpw = async () => {
    if (!cpOld || !cpNew || !cpConf) { toast('Fill all fields', 'err'); return; }
    if (cpNew !== cpConf) { toast('Passwords do not match', 'err'); return; }
    if (cpNew.length < 6) { toast('Min 6 chars', 'err'); return; }
    try {
      await apiLogin(user.username, cpOld);
      // Send OTP
      setCpOtpSending(true);
      const res = await requestOTP();
      setCpOtpEmail(res.email);
      setCpOtpStep(true);
      toast('OTP sent to your email');
    } catch (err) { toast(err.message || 'Wrong current password', 'err'); }
    finally { setCpOtpSending(false); }
  };

  const chpwVerify = async () => {
    if (!cpOtp) { toast('Enter OTP', 'err'); return; }
    try {
      await verifyOTPChangePassword(cpOtp, cpNew);
      setCpOld(''); setCpNew(''); setCpConf(''); setCpOtp(''); setCpOtpStep(false); setCpOtpEmail('');
      toast('Password updated successfully');
    } catch (err) { toast(err.message || 'Invalid OTP', 'err'); }
  };

  return (
    <div className="settings-page">
      <Card className="settings-card p-5 mb-4">
        <div className="text-sm font-semibold text-on-surface mb-4 pb-3" style={{ borderBottom: '1px solid rgba(196,198,207,0.5)' }}>School Information</div>
        <Grid>
          <Field label="School Name"><Input value={form.school || ''} onChange={sf('school')} /></Field>
          <Field label="Academic Year"><Input value={form.year || ''} onChange={sf('year')} placeholder="e.g. 2026-27" /></Field>
          <Field label="Report card year (marksheets)"><Input value={form.reportAcademicYear || ''} onChange={sf('reportAcademicYear')} placeholder="2025-2026" /></Field>
          <Field label="Principal"><Input value={form.prin || ''} onChange={sf('prin')} /></Field>
          <Field label="Phone"><Input value={form.phone || ''} onChange={sf('phone')} /></Field>
          <Span><Field label="Address"><Input value={form.addr || ''} onChange={sf('addr')} /></Field></Span>
        </Grid>
      </Card>

      <Card className="settings-card p-5 mb-4">
        <div className="text-sm font-semibold text-on-surface mb-4 pb-3" style={{ borderBottom: '1px solid rgba(196,198,207,0.5)' }}>Admin Accounts</div>
        <div className="settings-admin-list" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {db.admins.map((a, i) => (
            <div key={a._id || a.id || a.username || i} className="settings-admin-item" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 12, background: '#f8fafc', border: '1px solid #e8edf5' }}>
              {/* Avatar / pic */}
              <div style={{ width: 42, height: 42, borderRadius: '50%', flexShrink: 0, overflow: 'hidden', background: 'linear-gradient(135deg,#1960a3,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, color: '#fff', boxShadow: '0 2px 8px rgba(25,96,163,0.2)' }}>
                {a.pic
                  ? <img src={a.pic} alt={a.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : (a.name || a.username).trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase()
                }
              </div>
              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{a.name || a.username}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>@{a.username}</div>
              </div>
              {/* Role badge */}
              <span style={{ fontSize: 10, fontWeight: 700, color: '#1960a3', background: '#dbeafe', padding: '3px 10px', borderRadius: 20, letterSpacing: '0.04em', flexShrink: 0 }}>{a.role || 'Admin'}</span>
              {/* Main admin tag */}
              {a.username === 'admin' && <span style={{ fontSize: 10, fontWeight: 700, color: '#059669', background: '#d1fae5', padding: '3px 10px', borderRadius: 20, flexShrink: 0 }}>Main</span>}
              {/* Edit button — all admins */}
              <button className="settings-admin-btn" onClick={() => openEdit(a)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 150ms', flexShrink: 0 }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#1960a3'; e.currentTarget.style.color = '#1960a3'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#475569'; }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>edit</span> Edit
              </button>
              {/* Remove — only main admin can remove, can't remove self */}
              {isMainAdmin && a.username !== 'admin' && (
                <button className="settings-admin-btn" onClick={() => delUser(a)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: '1.5px solid #fee2e2', background: '#fff', color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 150ms', flexShrink: 0 }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 14 }}>delete</span> Remove
                </button>
              )}
            </div>
          ))}
        </div>
        {isMainAdmin && <div className="mt-4"><Btn variant="primary" size="sm" onClick={() => setAuOpen(true)}>+ Add User</Btn></div>}
      </Card>

      <Card className="settings-card p-5">
        <div className="text-sm font-semibold text-on-surface mb-4 pb-3" style={{ borderBottom: '1px solid rgba(196,198,207,0.5)' }}>Change Password</div>
        {!cpOtpStep ? (
          <>
            <Grid>
              <Field label="Current Password"><Input type="password" value={cpOld} onChange={setCpOld} /></Field>
              <Field label="New Password"><Input type="password" value={cpNew} onChange={setCpNew} /></Field>
              <Field label="Confirm New Password"><Input type="password" value={cpConf} onChange={setCpConf} /></Field>
            </Grid>
            <div className="mt-3"><Btn variant="primary" size="sm" onClick={chpw} disabled={cpOtpSending}>{cpOtpSending ? 'Sending OTP…' : 'Send OTP & Continue'}</Btn></div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>An OTP will be sent to your registered email to confirm the change.</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: '#475569', marginBottom: 14 }}>
              OTP sent to <b>{cpOtpEmail}</b>. Enter it below to confirm your password change.
            </div>
            <Field label="Enter OTP">
              <Input value={cpOtp} onChange={setCpOtp} placeholder="6-digit OTP" maxLength={6} />
            </Field>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <Btn variant="primary" size="sm" onClick={chpwVerify}>Verify & Update Password</Btn>
              <Btn variant="ghost" size="sm" onClick={() => { setCpOtpStep(false); setCpOtp(''); }}>Cancel</Btn>
            </div>
          </>
        )}
      </Card>

      {/* Add User Modal */}
      <Modal open={auOpen} onClose={() => setAuOpen(false)} title="Add User">
        <Grid>
          <Field label="Username *"><Input value={auForm.u || ''} onChange={v => setAuForm(f => ({ ...f, u: v }))} /></Field>
          <Field label="Display Name"><Input value={auForm.n || ''} onChange={v => setAuForm(f => ({ ...f, n: v }))} /></Field>
          <Field label="Password *"><Input type="password" value={auForm.p || ''} onChange={v => setAuForm(f => ({ ...f, p: v }))} /></Field>
          <Field label="Role"><Select value={auForm.r || 'Admin'} onChange={v => setAuForm(f => ({ ...f, r: v }))}><option>Admin</option><option>Accountant</option></Select></Field>
        </Grid>
        <ModalFooter><Btn onClick={() => setAuOpen(false)}>Cancel</Btn><Btn variant="primary" onClick={addUser}>Add</Btn></ModalFooter>
      </Modal>

      {/* Edit Admin Modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Edit Profile">
        {/* Profile picture upload */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ position: 'relative' }}>
            <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', background: 'linear-gradient(135deg,#1960a3,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 28, color: '#fff', boxShadow: '0 4px 16px rgba(25,96,163,0.25)' }}>
              {editForm.pic
                ? <img src={editForm.pic} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : (editForm.name || editForm.username || 'A').trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase()
              }
            </div>
            <label style={{ position: 'absolute', bottom: 0, right: 0, width: 26, height: 26, borderRadius: '50%', background: '#1960a3', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.2)', border: '2px solid #fff' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14, color: '#fff' }}>photo_camera</span>
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePicUpload} />
            </label>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>Click camera to upload photo (max 500KB)</div>
          {editForm.pic && (
            <button onClick={() => setEditForm(f => ({ ...f, pic: null }))}
              style={{ fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
              Remove photo
            </button>
          )}
        </div>
        <Grid>
          <Field label="Display Name *"><Input value={editForm.name || ''} onChange={v => setEditForm(f => ({ ...f, name: v }))} /></Field>
          <Field label="Username"><Input value={editForm.username || ''} disabled style={{ opacity: 0.6 }} /></Field>
          <Field label="Role">
            <Select value={editForm.role || 'Admin'} onChange={v => setEditForm(f => ({ ...f, role: v }))}>
              <option>Admin</option><option>Accountant</option><option>Principal</option>
            </Select>
          </Field>
        </Grid>

        {/* Email change section */}
        <div style={{ marginTop: 16, padding: '14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: emailOtpStep ? 12 : 0 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 2 }}>EMAIL (FOR OTP)</div>
              <div style={{ fontSize: 13, color: '#0f172a' }}>{editForm.email || <span style={{ color: '#94a3b8' }}>Not set</span>}</div>
            </div>
            {!emailOtpStep && (
              <Btn size="sm" onClick={sendEmailChangeOtp} disabled={emailOtpSending}>
                {emailOtpSending ? 'Sending…' : editForm.email ? 'Change Email' : 'Add Email'}
              </Btn>
            )}
          </div>
          {emailOtpStep === 'verify-current' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 12, color: '#0369a1', fontWeight: 600 }}>Step 1: Verify current email OTP</div>
              <Input value={emailOtpVal} onChange={setEmailOtpVal} placeholder="Enter OTP sent to current email" maxLength={6} />
              <Input type="email" value={emailNew} onChange={setEmailNew} placeholder="Enter new email address" />
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn variant="primary" size="sm" onClick={verifyCurrentAndSendNew}>Verify & Send OTP to New Email</Btn>
                <Btn size="sm" onClick={() => { resetEmailFlow(); setEditForm(f => ({ ...f, email: emailPrev })); }}>Cancel</Btn>
              </div>
            </div>
          )}
          {emailOtpStep === 'verify-new' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>Step 2: Verify new email — OTP sent to <b>{emailNew}</b></div>
              <Input value={emailOtpVal} onChange={setEmailOtpVal} placeholder="Enter OTP sent to new email" maxLength={6} />
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn variant="primary" size="sm" onClick={verifyEmailAndSave}>Confirm & Save</Btn>
                <Btn size="sm" onClick={() => { setEmailOtpStep('verify-current'); setEmailOtpVal(''); }}>Wrong email? Go back</Btn>
              </div>
              <button onClick={() => setEmailOtpStep('recover')} style={{ fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', textDecoration: 'underline' }}>
                Reset with password instead
              </button>
            </div>
          )}
          {emailOtpStep === 'enter-new' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 12, color: '#0369a1', fontWeight: 600 }}>Enter your email address:</div>
              <Input type="email" value={emailNew} onChange={setEmailNew} placeholder="your@gmail.com" />
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn variant="primary" size="sm" onClick={saveEmailDirect}>Save Email</Btn>
                <Btn size="sm" onClick={resetEmailFlow}>Cancel</Btn>
              </div>
            </div>
          )}
          {emailOtpStep === 'recover' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>Reset email using admin password:</div>
              <Input type="password" value={emailRecoveryPw} onChange={setEmailRecoveryPw} placeholder="Current admin password" />
              <Input type="email" value={emailNew} onChange={setEmailNew} placeholder="Correct email address" />
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn variant="primary" size="sm" onClick={recoverEmail}>Reset Email</Btn>
                <Btn size="sm" onClick={resetEmailFlow}>Cancel</Btn>
              </div>
            </div>
          )}
        </div>

        <ModalFooter>
          <Btn onClick={() => { setEditOpen(false); resetEmailFlow(); }}>Cancel</Btn>
          <Btn variant="primary" onClick={saveEdit}>Save Changes</Btn>
        </ModalFooter>
      </Modal>
    </div>
  );
}


export default Settings;
